import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { musicServicesByMusicAPIID } from 'fym-shared/src/music-services/allMusicServices';
import { TypedMemo } from '../../common/types';
import { useGetArtistPortalOverviewQuery, useGetConnectionInsightsQuery } from '../../../redux/api/fymAPI';
import { HomeKpiTile } from './HomeKpiTile';
import { HomeKpiSkeleton } from './HomeKpiSkeleton';
import { HomeRowError } from './HomeRowError';
import { HomeKpiSetupTile } from './HomeKpiSetupTile';
import { artistHomeMessages as messages } from './artistHomeMessages';
import { AudienceTileModel, audienceTiles } from './audienceTiles';
import { useAudienceTileText } from './useAudienceTileText';
import { HomePeriodDays } from './homePeriod';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Home; each part uses its own slice of it */
import styles from './ArtistHome.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface AudienceRowProps {
  accountId: number;
  days: HomePeriodDays;
  /** The Catalog, where the profile links and the portal sign-ins live. */
  onOpenCatalog: () => void;
}

const KPI_COLUMNS = 4;

/**
 * The Audience row (design a1): followers per read service and the monthly listeners. A service
 * that is not read has no tile. With one service or none, a dashed "More services" tile fills the row.
 */
const AudienceRowComponent: React.FC<AudienceRowProps> = ({ accountId, days, onOpenCatalog }) => {
  const intl = useIntl();
  const tileText = useAudienceTileText();
  const insights = useGetConnectionInsightsQuery({ accountId, days });
  // The same read as the Streams row, so the cache answers it once.
  const overview = useGetArtistPortalOverviewQuery({ accountId, days });

  const tiles = useMemo(
    () => audienceTiles(insights.data?.insights ?? [], overview.data),
    [insights.data, overview.data]
  );
  const followerTileCount = useMemo(
    () => tiles.filter((tile) => tile.labelKind !== 'monthlyListeners').length,
    [tiles]
  );
  const retry = useCallback(() => {
    insights.refetch().catch(console.error);
  }, [insights]);

  if (insights.isLoading) {
    return (
      <div className={styles.kpis}>
        {Array.from({ length: KPI_COLUMNS }, (_, index) => (
          <HomeKpiSkeleton key={index} />
        ))}
      </div>
    );
  }

  if (insights.error !== undefined) {
    return <HomeRowError error={insights.error} retry={retry} />;
  }

  const renderTile = (tile: AudienceTileModel) => {
    const text = tileText(tile);
    return (
      <HomeKpiTile
        key={tile.key}
        label={text.label}
        dotColor={musicServicesByMusicAPIID[tile.integrationType]?.color.primary}
        value={tile.value}
        delta={text.delta}
        source={text.source}
        onOpen={onOpenCatalog}
      />
    );
  };

  return (
    <div className={styles.kpis}>
      {tiles.map(renderTile)}
      {followerTileCount <= 1 && (
        <HomeKpiSetupTile
          title={intl.formatMessage(messages.moreServicesTitle)}
          body={intl.formatMessage(messages.moreServicesBody)}
          cta={intl.formatMessage(messages.moreServicesCta)}
          span={Math.max(KPI_COLUMNS - tiles.length, 1)}
          onAction={onOpenCatalog}
        />
      )}
    </div>
  );
};

export const AudienceRow = TypedMemo(AudienceRowComponent);
